import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Container, Paper, TextField, Typography } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Cookies from "js-cookie";
import Navbar from "../../Navbar/Navbar";
import BgImage from "../../assets/shipimage.jpg";
import baseURL from "../../config";
import CircularProgress from '@mui/material/CircularProgress';


const ForgotPassword = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [showFields, setShowFields] = useState(false);
    const [otp, setOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Fetch email from Cookies (if stored during login)
        const storedEmail = Cookies.get("userEmail");
        if (storedEmail) {
            setEmail(storedEmail);
        }
    }, []);


    const handleSendOtp = async () => {
        if (!email) {
            toast.error("Email is required!");
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(`${baseURL}/api/forgot-password`, { email });

            if (response.status === 200) {
                toast.success("OTP sent to your email!");
                setShowFields(true); // Show OTP + Password fields
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to send OTP!");
            console.error("Forgot Password Error:", error);
        } finally {
            setLoading(false); // Stop loading
        }
    };


    const handlePasswordReset = async () => {
        if (otp.length !== 6) {
            toast.error("Enter a valid 6-digit OTP!");
            return;
        }

        if (!newPassword || !confirmPassword) {
            toast.error("Both password fields are required!");
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error("Passwords do not match!");
            return;
        }

        try {
            const response = await axios.post(`${baseURL}/api/reset-password`, {
                email,
                otp,
                newPassword
            });

            if (response.status === 200) {
                toast.success("Password Reset Successful! Redirecting...");
                setTimeout(() => navigate("/login"), 2000); // Redirect to login page
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Password reset failed!");
            console.error("Reset Password Error:", error);
        }
    };

    return (
        <>
            <Navbar />

            <Box
                sx={{
                    width: "100%",
                    height: "100vh",
                    background: `url(${BgImage}) center/cover no-repeat`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    position: "relative",
                    zIndex: 1,
                    pt: 10,
                }}
            >
                <Container maxWidth="xs">
                    <Paper
                        elevation={8}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            backdropFilter: "blur(8px)",
                            bgcolor: "rgba(255, 255, 255, 0.9)",
                            textAlign: "center",
                        }}
                    >
                        <Typography variant="h5" fontWeight="bold" mb={2}>
                            Forgot Password
                        </Typography>

                        <TextField
                            fullWidth
                            label="Email"
                            variant="outlined"
                            margin="normal"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="off"
                            sx={{
                                "& .MuiInputBase-input": {
                                    textAlign: "center",
                                    fontSize: "1rem",
                                }
                            }}
                        />

                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100px" }}>
                                <CircularProgress color="primary" />
                            </Box>
                        ) : !showFields ? (
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                sx={{ mt: 2 }}
                                onClick={handleSendOtp}
                            >
                                Send OTP
                            </Button>
                        ) : (
                            <>
                                {/* OTP Field */}
                                <TextField
                                    fullWidth
                                    label="Enter OTP"
                                    variant="outlined"
                                    margin="normal"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    inputProps={{ maxLength: 6, style: { textAlign: "center", fontSize: "1rem" } }}
                                />

                                {/* New Password Field */}
                                <TextField
                                    fullWidth
                                    label="New Password"
                                    type="password"
                                    variant="outlined"
                                    margin="normal"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />

                                {/* Confirm Password Field */}
                                <TextField
                                    fullWidth
                                    label="Confirm Password"
                                    type="password"
                                    variant="outlined"
                                    margin="normal"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />

                                <Button
                                    variant="contained"
                                    color="secondary"
                                    fullWidth
                                    sx={{ mt: 2 }}
                                    onClick={handlePasswordReset}
                                >
                                    Reset Password
                                </Button>
                            </>
                        )}
                    </Paper>
                </Container>
            </Box>

            <ToastContainer position="top-right" autoClose={3000} />
        </>
    );
};

export default ForgotPassword;
